import { XMLParser } from 'fast-xml-parser';
import { isEmpty, map, forEach, split } from 'lodash';
import { ExportToCsv } from 'export-to-csv';
import { csvConfigExport } from './export';
import { FPS_VIEW_XML_CONFIG } from '@nxsys/constants/AppConstants';

const parseErrorXMLResponse = (xml: string) => {
  const parser = new XMLParser();
  const xmlJs = parser.parse(xml);
  let errorsDetail =
    xmlJs?.['GovTalkMessage']?.['Body']?.['ErrorResponse']?.['Error'];
  const governmentError =
    xmlJs?.['GovTalkMessage']?.['GovTalkDetails']?.['GovTalkErrors']?.[
      'Error'
    ] || {};
  if (!isEmpty(errorsDetail) && !Array.isArray(errorsDetail)) {
    errorsDetail = [errorsDetail];
  }
  return [errorsDetail, governmentError];
};

const parseEmployeeFromXMLRequest = (xml: string) => {
  const parser = new XMLParser();
  const xmlRequestParse = parser.parse(xml);
  let employees =
    xmlRequestParse?.['GovTalkMessage']?.['Body']?.['IRenvelope']?.[
      'FullPaymentSubmission'
    ]?.['Employee'];
  if (!isEmpty(employees) && !Array.isArray(employees)) {
    employees = [employees];
  }
  const employeePayIds = map(
    employees,
    (employee) => employee?.['Employment']?.['PayId'],
  );
  return employeePayIds;
};

export const onNewExportXMLErrors = (errors: any[]) => {
  const dataExport: any[] = [];
  errors.forEach((err) => {
    const taxWeek = `${err?.taxWeek}/${err?.currentYear}`;

    // Use pre-parsed errors from backend when available (bulk export API nullifies xmlResponse)
    if (err?.parsedErrors?.length > 0) {
      err.parsedErrors.forEach((parsed: any) => {
        dataExport.push({
          taxWeek,
          raisedBy: parsed.raisedBy || '',
          number: parsed.number || '',
          type: parsed.type || '',
          text: parsed.text || '',
          location: parsed.location || '',
          employeePayId: parsed.employeePayId || '',
          employeeName: parsed.employeeName || '',
        });
      });
      return;
    }

    // Fallback: parse from XML response (single-record exports that still have xmlResponse)
    const [errorsDetail, governmentError] = parseErrorXMLResponse(
      err?.xmlResponse,
    );
    if (isEmpty(errorsDetail)) {
      const raisedBy = governmentError?.RaisedBy || '';
      const number = governmentError?.Number || '';
      const type = governmentError?.Type || '';
      const text = governmentError?.Text || '';
      const location = governmentError?.Location || '';
      dataExport.push({
        taxWeek,
        raisedBy,
        number,
        type,
        text,
        location,
        employeePayId: '',
        employeeName: '',
      });
    } else {
      let employees = err?.employmentIds ?? [];
      if (!!err?.xmlRequest) {
        employees = parseEmployeeFromXMLRequest(err?.xmlRequest);
      }
      forEach(errorsDetail, (errData) => {
        const raisedBy = errData?.RaisedBy || '';
        const number = errData?.Number || '';
        const type = errData?.Type || '';
        const text = errData?.Text || '';
        const location = errData?.Location || '';
        let employeePayId = '';
        const parseLocation = split(location, '/fps:Employee');
        const positionEmployee = Number(parseLocation?.[1]?.slice(1, 2) ?? -1);
        employeePayId =
          positionEmployee >= 0 ? employees[positionEmployee - 1] : '';
        dataExport.push({
          taxWeek,
          raisedBy,
          number,
          type,
          text,
          location,
          employeePayId,
          employeeName: '',
        });
      });
    }
  });
  const headers = [
    'Tax Week',
    'Raised By',
    'Number',
    'Type',
    'Error',
    'Location',
    'Employee PayId',
    'Employee Name',
  ];
  const exportName = csvConfigExport(`Error_data_detail`, headers);
  const csvExporter = new ExportToCsv(exportName);
  csvExporter.generateCsv(dataExport);
};

export const updateNodeFunc = (node: any) => {
  const { tag, attributes, children } = node;
  let newChildren = [...children];
  let isNeedUpdateViewXML = false;
  if (tag === 'FullPaymentSubmission') {
    const empRefNode = children?.find((child: any) => child.tag === 'EmpRefs');
    const taxYearNode = children?.find(
      (child: any) => child.tag === 'RelatedTaxYear',
    );
    const employeeNodeList = children?.filter(
      (child: any) => child.tag === 'Employee',
    );
    if (
      employeeNodeList.length >
      FPS_VIEW_XML_CONFIG.ITEMS_RENDER +
        FPS_VIEW_XML_CONFIG.ITEMS_RENDER_BEFORE_AFTER * 2
    )
      newChildren = [
        empRefNode,
        taxYearNode,
        {
          attributes: '',
          children: employeeNodeList,
          tag: 'EmployeeList',
        },
      ];
  }
  if (tag === 'EmployeeList')
    isNeedUpdateViewXML =
      children.length >
      FPS_VIEW_XML_CONFIG.ITEMS_RENDER +
        FPS_VIEW_XML_CONFIG.ITEMS_RENDER_BEFORE_AFTER * 2;

  return {
    tag,
    attributes,
    children: newChildren,
    isNeedUpdateViewXML,
  };
};

export const parseXML = (xmlString: string) => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

  const parseNode = (node: any): any => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.nodeValue.trim();
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      return {
        tag: node.tagName,
        attributes: Array.from(node.attributes)
          .map((attr: any) => `${attr.name}="${attr.value}"`)
          .join(' '),
        children: Array.from(node.childNodes).map(parseNode).filter(Boolean),
      };
    }
    return null;
  };

  return parseNode(xmlDoc.documentElement);
};

export const downloadXML = ({
  filename,
  xmlFile,
}: {
  filename: string;
  xmlFile: string;
}) => {
  const element = document.createElement('a');
  const file = new Blob([xmlFile], { type: 'text/xml' });
  element.href = URL.createObjectURL(file);
  element.download = filename;
  document.body.appendChild(element);
  element.click();
};

export const submissionSummaryFields = [
  {
    label: '',
    fields: [
      {
        label: 'common.envelopeVersion',
        fieldName: 'EnvelopeVersion',
      },
    ],
  },
  {
    label: 'common.messageDetail',
    fields: [
      {
        label: 'common.class',
        fieldName: 'Class',
      },
      {
        label: 'common.qualifier',
        fieldName: 'Qualifier',
      },
      {
        label: 'common.function',
        fieldName: 'Function',
      },
      {
        label: 'common.transformation',
        fieldName: 'Transformation',
      },
      {
        label: 'common.gatewayTest',
        fieldName: 'GatewayTest',
      },
    ],
  },
  {
    label: 'common.senderDetail',
    fields: [
      {
        label: 'common.senderID',
        fieldName: 'SenderID',
      },
      {
        label: 'common.authenMethod',
        fieldName: 'Method',
      },
      {
        label: 'common.authenRole',
        fieldName: 'Role',
      },
      {
        label: 'common.authenValue',
        fieldName: 'Value',
      },
    ],
  },
  {
    label: 'common.channelDetail',
    fields: [
      {
        label: 'common.uri',
        fieldName: 'URI',
      },
      {
        label: 'common.product',
        fieldName: 'Product',
      },
      {
        label: 'common.version',
        fieldName: 'Version',
      },
    ],
  },
  {
    label: 'common.taxDetail',
    fields: [
      {
        label: 'common.taxOfficeNumber',
        fieldName: 'TaxOfficeNumber',
      },
      {
        label: 'common.taxOfficeReference',
        fieldName: 'TaxOfficeReference',
      },
      {
        label: 'common.periodEnd',
        fieldName: 'PeriodEnd',
      },
      {
        label: 'common.defaultCurrency',
        fieldName: 'DefaultCurrency',
      },
      {
        label: 'common.IRmark',
        fieldName: 'IRmark',
      },
      {
        label: 'common.sender',
        fieldName: 'Sender',
      },
    ],
  },
  {
    label: 'common.employerDetail',
    fields: [
      {
        label: 'common.officeNo',
        fieldName: 'OfficeNo',
      },
      {
        label: 'common.payeRef',
        fieldName: 'PayeRef',
      },
      {
        label: 'common.AORef',
        fieldName: 'AORef',
      },
      {
        label: 'common.relatedTaxYear',
        fieldName: 'RelatedTaxYear',
      },
    ],
  },
];

export const downloadXMLFile = (xmlFile: string, filename: string) => {
  const element = document.createElement('a');
  const file = new Blob([xmlFile], { type: 'text/xml' });
  element.href = URL.createObjectURL(file);
  element.download = filename;
  document.body.appendChild(element);
  element.click();
};

export const submissionSummaryFieldsEPS = [
  {
    label: '',
    fields: [
      {
        label: 'common.envelopeVersion',
        fieldName: 'EnvelopeVersion',
      },
    ],
  },
  {
    label: 'common.messageDetail',
    fields: [
      {
        label: 'common.class',
        fieldName: 'Class',
      },
      {
        label: 'common.qualifier',
        fieldName: 'Qualifier',
      },
      {
        label: 'common.function',
        fieldName: 'Function',
      },
      {
        label: 'common.transformation',
        fieldName: 'Transformation',
      },
      {
        label: 'common.gatewayTest',
        fieldName: 'GatewayTest',
      },
    ],
  },
  {
    label: 'common.senderDetail',
    fields: [
      {
        label: 'common.senderID',
        fieldName: 'SenderID',
      },
      {
        label: 'common.authenMethod',
        fieldName: 'Method',
      },
      {
        label: 'common.authenRole',
        fieldName: 'Role',
      },
      {
        label: 'common.authenValue',
        fieldName: 'Value',
      },
    ],
  },
  {
    label: 'common.channelDetail',
    fields: [
      {
        label: 'common.uri',
        fieldName: 'URI',
      },
      {
        label: 'common.product',
        fieldName: 'Product',
      },
      {
        label: 'common.version',
        fieldName: 'Version',
      },
    ],
  },
  {
    label: 'common.taxDetail',
    fields: [
      {
        label: 'common.taxOfficeNumber',
        fieldName: 'TaxOfficeNumber',
      },
      {
        label: 'common.taxOfficeReference',
        fieldName: 'TaxOfficeReference',
      },
      {
        label: 'common.periodEnd',
        fieldName: 'PeriodEnd',
      },
      {
        label: 'common.defaultCurrency',
        fieldName: 'DefaultCurrency',
      },
      {
        label: 'common.IRmark',
        fieldName: 'IRmark',
      },
      {
        label: 'common.sender',
        fieldName: 'Sender',
      },
    ],
  },
  {
    label: 'common.employerDetail',
    fields: [
      {
        label: 'common.officeNo',
        fieldName: 'OfficeNo',
      },
      {
        label: 'common.payeRef',
        fieldName: 'PayeRef',
      },
      {
        label: 'common.AORef',
        fieldName: 'AORef',
      },
      {
        label: 'common.noPaymentForPeriod',
        fieldName: 'NoPaymentForPeriod',
      },
      {
        label: 'common.noPaymentDatesFrom',
        fieldName: 'From',
      },
      {
        label: 'common.noPaymentDatesTo',
        fieldName: 'To',
      },
      {
        label: 'common.empAllceInd',
        fieldName: 'EmpAllceInd',
      },
      {
        label: 'common.taxMonth',
        fieldName: 'TaxMonth',
      },
      {
        label: 'common.smpRecovered',
        fieldName: 'SMPRecovered',
      },
      {
        label: 'common.sppRecovered',
        fieldName: 'SPPRecovered',
      },
      {
        label: 'common.sapRecovered',
        fieldName: 'SAPRecovered',
      },
      {
        label: 'common.shppRecovered',
        fieldName: 'ShPPRecovered',
      },
      {
        label: 'common.spbpRecovered',
        fieldName: 'SPBPRecovered',
      },
      {
        label: 'common.nicCompensationOnSMP',
        fieldName: 'NICCompensationOnSMP',
      },
      {
        label: 'common.nicCompensationOnSPP',
        fieldName: 'NICCompensationOnSPP',
      },
      {
        label: 'common.nicCompensationOnSAP',
        fieldName: 'NICCompensationOnSAP',
      },
      {
        label: 'common.nicCompensationOnShPP',
        fieldName: 'NICCompensationOnShPP',
      },
      {
        label: 'common.nicCompensationOnSPBP',
        fieldName: 'NICCompensationOnSPBP',
      },
      {
        label: 'common.cisDeductionsSuffered',
        fieldName: 'CISDeductionsSuffered',
      },
      {
        label: 'common.levyDueYTD',
        fieldName: 'LevyDueYTD',
      },
      {
        label: 'common.annualAllce',
        fieldName: 'AnnualAllce',
      },
      {
        label: 'common.accountHoldersName',
        fieldName: 'AccountHoldersName',
      },
      {
        label: 'common.accountNo',
        fieldName: 'AccountNo',
      },
      {
        label: 'common.sortCode',
        fieldName: 'SortCode',
      },
      {
        label: 'common.relatedTaxYear',
        fieldName: 'RelatedTaxYear',
      },
    ],
  },
];
